import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

const schema = yup.object({
  nameEn: yup
    .string()
    .trim()
    .required('English tag is required')
    .max(100, 'English tag should be at most 100 characters'),
  nameNo: yup
    .string()
    .trim()
    .required('Norwegian tag is required')
    .max(100, 'Norwegian tag should be at most 100 characters'),
  displayOrder: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value
    )
    .typeError('Sequence number should be a number')
    .integer('Sequence number should be a whole number')
    .min(0, 'Sequence number should be at least 0')
    .max(100000, 'Sequence number should be at most 100000')
    .required('Sequence number is required'),
});

const getDefaultValues = (tag, defaultDisplayOrder) => ({
  nameEn: tag?.nameEn ?? '',
  nameNo: tag?.nameNo ?? '',
  displayOrder: tag?.displayOrder ?? defaultDisplayOrder,
});

const TagManagementDialog = ({
  open,
  onClose,
  onSubmit,
  tag,
  defaultDisplayOrder,
  isSubmitting,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(tag, defaultDisplayOrder),
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultValues(tag, defaultDisplayOrder));
    }
  }, [defaultDisplayOrder, open, reset, tag]);

  const submitForm = (data) => {
    onSubmit({
      nameEn: data.nameEn.trim(),
      nameNo: data.nameNo.trim(),
      displayOrder: Number(data.displayOrder),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth='md'
    >
      <DialogContent sx={{ p: 0 }}>
        <Stack px={4} py={3} rowGap={3}>
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='s20_w500' component='span'>
              <strong>Tag Management:</strong>{' '}
              {tag ? 'Edit tag' : 'Add a new tag'}
            </Typography>
            <IconButton onClick={onClose} disabled={isSubmitting}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          <form onSubmit={handleSubmit(submitForm)}>
            <Stack rowGap={3}>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                <Box flex={1}>
                  <Stack direction='row' gap={1} alignItems='center' mb={1}>
                    <Chip
                      label='EN'
                      size='small'
                      sx={{
                        backgroundColor: '#EEF4FF',
                        color: '#1A3A7A',
                        fontWeight: 700,
                      }}
                    />
                    <Typography variant='s16_w600'>Add English Tag</Typography>
                  </Stack>
                  <Controller
                    name='nameEn'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder='Tag Name'
                        error={!!errors.nameEn}
                        helperText={errors.nameEn?.message}
                      />
                    )}
                  />
                </Box>

                <Box flex={1}>
                  <Stack direction='row' gap={1} alignItems='center' mb={1}>
                    <Chip
                      label='NW'
                      size='small'
                      sx={{
                        backgroundColor: '#FFF3E8',
                        color: '#C25A1B',
                        fontWeight: 700,
                      }}
                    />
                    <Typography variant='s16_w600'>Add Norwegian Tag</Typography>
                  </Stack>
                  <Controller
                    name='nameNo'
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder='Tag Name'
                        error={!!errors.nameNo}
                        helperText={errors.nameNo?.message}
                      />
                    )}
                  />
                </Box>
              </Stack>

              <Box maxWidth='24rem'>
                <Typography variant='s16_w600' mb={1}>
                  Sequence number
                </Typography>
                <Controller
                  name='displayOrder'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type='number'
                      placeholder='Sequence number'
                      error={!!errors.displayOrder}
                      helperText={
                        errors.displayOrder?.message ??
                        'Lower sequence numbers show first in the app.'
                      }
                      inputProps={{ min: 0, max: 100000, step: 1 }}
                    />
                  )}
                />
              </Box>

              <Stack direction='row' gap={2} justifyContent='flex-start'>
                <Button variant='contained' type='submit' disabled={isSubmitting}>
                  Save
                </Button>
                <Button variant='outlined' onClick={onClose} disabled={isSubmitting}>
                  Discard
                </Button>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default TagManagementDialog;
